/**
 * Endpoints de la API, tipados desde el `openapi.json` del backend.
 *
 * El archivo `api-types.ts` **se genera** con `pnpm api:types` a partir del
 * esquema que publica FastAPI. Si el backend cambia un contrato, el frontend
 * deja de compilar: es exactamente lo que se buscaba al elegir Pydantic →
 * OpenAPI → TypeScript (ADR-0002).
 */

import { apiRequest, apiRequestWithHeaders } from "./api";
import type { components } from "./api-types";
import { clearTenantLocalCache } from "./offline/db";
import { getSession, setSession, type SessionResponse } from "./session";

type Schemas = components["schemas"];

export type LoginRequest = Schemas["LoginRequest"];
export type SaleCreate = Schemas["SaleCreate"];
export type SalePublic = Schemas["SalePublic"];
export type SaleList = Schemas["SaleList"];
export type RefundCreate = Schemas["RefundCreate"];
export type RefundPublic = Schemas["RefundPublic"];
export type ProductList = Schemas["ProductList"];
export type ProductPublic = Schemas["ProductPublic"];
export type ProductCreate = Schemas["ProductCreate"];
export type ProductUpdate = Schemas["ProductUpdate"];
export type CategoryPublic = Schemas["CategoryPublic"];
export type CategoryCreate = Schemas["CategoryCreate"];
export type UnitPublic = Schemas["UnitPublic"];
export type TaxPublic = Schemas["TaxPublic"];
export type PriceListPublic = Schemas["PriceListPublic"];
export type SearchResults = Schemas["SearchResults"];
export type SearchHit = Schemas["SearchHit"];
export type ResolvedPrice = Schemas["ResolvedPrice"];
export type CashTotalsPublic = Schemas["CashTotalsPublic"];
export type SessionPublic = Schemas["SessionPublic"];
export type SessionDetail = Schemas["SessionDetail"];
export type SessionOpen = Schemas["SessionOpen"];
export type SessionClose = Schemas["SessionClose"];
export type ManualMovement = Schemas["ManualMovement"];
export type WarehousePublic = Schemas["WarehousePublic"];
export type BranchPublic = Schemas["BranchPublic"];
export type TenantPublic = Schemas["TenantPublic"];

// ---------------------------------------------------------------------------
// Autenticación
// ---------------------------------------------------------------------------

export async function login(payload: LoginRequest): Promise<SessionResponse> {
  const previousTenantId = getSession()?.tenant?.id;
  const session = await apiRequest<SessionResponse>("/api/v1/auth/login", {
    method: "POST",
    body: payload,
    skipAuthRefresh: true,
  });
  if (previousTenantId !== session.tenant.id) {
    await clearTenantLocalCache();
  }
  setSession(session);
  return session;
}

export async function logout(refreshToken: string): Promise<void> {
  await clearTenantLocalCache();
  await apiRequest<void>("/api/v1/auth/logout", {
    method: "POST",
    body: { refresh_token: refreshToken },
    skipAuthRefresh: true,
  });
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------

export function searchProducts(term: string, branchId?: string): Promise<SearchResults> {
  const params = new URLSearchParams({ q: term });
  if (branchId) params.set("branch_id", branchId);
  return apiRequest<SearchResults>(`/api/v1/search?${params.toString()}`);
}

/** Listar y buscar productos con filtros y paginación. */
export function listProducts(params: {
  q?: string;
  category_id?: string;
  kind?: "producto" | "servicio";
  only_active?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<ProductList> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  if (params.q) query.set("q", params.q);
  if (params.category_id) query.set("category_id", params.category_id);
  if (params.kind) query.set("kind", params.kind);
  if (params.only_active !== undefined) query.set("only_active", String(params.only_active));
  return apiRequest<ProductList>(`/api/v1/products?${query.toString()}`);
}

export function createProduct(payload: ProductCreate): Promise<ProductPublic> {
  return apiRequest<ProductPublic>("/api/v1/products", {
    method: "POST",
    body: payload,
  });
}

export function updateProduct(productId: string, payload: ProductUpdate): Promise<ProductPublic> {
  return apiRequest<ProductPublic>(`/api/v1/products/${productId}`, {
    method: "PATCH",
    body: payload,
  });
}

export function listCategories(onlyActive = true): Promise<CategoryPublic[]> {
  return apiRequest<CategoryPublic[]>(`/api/v1/categories?only_active=${onlyActive}`);
}

export function createCategory(payload: CategoryCreate): Promise<CategoryPublic> {
  return apiRequest<CategoryPublic>("/api/v1/categories", {
    method: "POST",
    body: payload,
  });
}

export function listUnits(onlyActive = true): Promise<UnitPublic[]> {
  return apiRequest<UnitPublic[]>(`/api/v1/units?only_active=${onlyActive}`);
}

export function listTaxes(onlyActive = true): Promise<TaxPublic[]> {
  return apiRequest<TaxPublic[]>(`/api/v1/taxes?only_active=${onlyActive}`);
}

export function listPriceLists(): Promise<PriceListPublic[]> {
  return apiRequest<PriceListPublic[]>("/api/v1/price-lists");
}

export function resolvePrice(
  variantId: string,
  options: { quantity?: string; branchId?: string } = {},
): Promise<ResolvedPrice> {
  const params = new URLSearchParams();
  if (options.quantity) params.set("quantity", options.quantity);
  if (options.branchId) params.set("branch_id", options.branchId);
  const query = params.toString();
  return apiRequest<ResolvedPrice>(
    `/api/v1/variants/${variantId}/price${query ? `?${query}` : ""}`,
  );
}

// ---------------------------------------------------------------------------
// Caja
// ---------------------------------------------------------------------------

export function openCashSession(payload: SessionOpen): Promise<SessionPublic> {
  return apiRequest<SessionPublic>("/api/v1/cash/sessions", { method: "POST", body: payload });
}

export function currentCashTotals(): Promise<CashTotalsPublic> {
  return apiRequest<CashTotalsPublic>("/api/v1/cash/sessions/current");
}

export function cashSessionDetail(sessionId: string): Promise<SessionDetail> {
  return apiRequest<SessionDetail>(`/api/v1/cash/sessions/${sessionId}`);
}

export function closeCashSession(
  sessionId: string,
  payload: SessionClose,
): Promise<CashTotalsPublic> {
  return apiRequest<CashTotalsPublic>(`/api/v1/cash/sessions/${sessionId}/close`, {
    method: "POST",
    body: payload,
  });
}

export function registerCashMovement(payload: ManualMovement): Promise<unknown> {
  return apiRequest("/api/v1/cash/movements", { method: "POST", body: payload });
}

// ---------------------------------------------------------------------------
// Inventario
// ---------------------------------------------------------------------------

export function listWarehouses(branchId?: string): Promise<WarehousePublic[]> {
  const query = branchId ? `?branch_id=${branchId}` : "";
  return apiRequest<WarehousePublic[]>(`/api/v1/warehouses${query}`);
}

// ---------------------------------------------------------------------------
// Ventas
// ---------------------------------------------------------------------------

export function listSales(params: { limit?: number; offset?: number } = {}): Promise<SaleList> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
  });
  return apiRequest<SaleList>(`/api/v1/sales?${query.toString()}`);
}

export function voidSale(
  saleId: string,
  reason: string,
  idempotencyKey: string,
): Promise<SalePublic> {
  return apiRequest<SalePublic>(`/api/v1/sales/${saleId}/void`, {
    method: "POST",
    body: { reason },
    idempotencyKey,
  });
}

export { apiRequestWithHeaders };

/** Sucursal activa guardada en la sesión. */
export function activeBranch(): BranchPublic | null {
  const session = getSession();
  return session?.branches[0] ?? null;
}

// ---------------------------------------------------------------------------
// Solicitudes de Contacto y Pruebas Gratuitas
// ---------------------------------------------------------------------------

export interface ContactInquiryPayload {
  full_name: string;
  email: string;
  phone?: string | null;
  business_name?: string | null;
  business_type?: string | null;
  is_free_trial_requested?: boolean;
  message?: string | null;
}

export interface ContactInquiryItem {
  id: string;
  full_name: string;
  email: string;
  phone?: string | null;
  business_name?: string | null;
  business_type?: string | null;
  is_free_trial_requested: boolean;
  message?: string | null;
  status: string;
  email_sent: boolean;
  email_error?: string | null;
  created_at: string;
}

export function submitContactInquiry(payload: ContactInquiryPayload): Promise<ContactInquiryItem> {
  return apiRequest<ContactInquiryItem>("/api/v1/public/contact", {
    method: "POST",
    body: payload,
    skipAuthRefresh: true,
  });
}

export function listContactInquiries(params: { limit?: number; offset?: number } = {}): Promise<ContactInquiryItem[]> {
  const query = new URLSearchParams({
    limit: String(params.limit ?? 100),
    offset: String(params.offset ?? 0),
  });
  return apiRequest<ContactInquiryItem[]>(`/api/v1/saas/admin/inquiries?${query.toString()}`);
}

export function updateContactInquiryStatus(
  inquiryId: string,
  status: string,
): Promise<ContactInquiryItem> {
  return apiRequest<ContactInquiryItem>(`/api/v1/saas/admin/inquiries/${inquiryId}`, {
    method: "PATCH",
    body: { status },
  });
}

